import { useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { Plus, Minus, Trash, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CouponService from '../services/coupon.service';
import { Button } from '../components/ui/Button';
import { InputField } from '../components/ui/InputField';
import { formatPrice } from '../utils/currency';

const CartPage = () => {
    const { cart, updateQuantity, removeFromCart, fetchCart } = useCart();
    const navigate = useNavigate();

    const [couponCode, setCouponCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [couponError, setCouponError] = useState('');
    const [couponSuccess, setCouponSuccess] = useState('');

    useEffect(() => {
        fetchCart();
    }, []);

    const handleApplyCoupon = async () => {
        if (!couponCode) return;
        try {
            const res = await CouponService.validateCoupon(couponCode, cart.total_price);
            setDiscount(parseFloat(res.discount_amount));
            setCouponSuccess(`Code applied: -${formatPrice(res.discount_amount)}`);
            setCouponError('');
        } catch (err) {
            setCouponError(err.response?.data?.error || 'Invalid coupon');
            setCouponSuccess('');
            setDiscount(0);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
            <h1 className="font-serif text-4xl mb-12">Shopping Bag</h1>

            {(!cart.items || cart.items.length === 0) ? (
                <div className="text-center py-32 bg-gray-50 rounded-sm">
                    <p className="text-gray-500 mb-6 uppercase tracking-widest text-sm">Your bag is empty</p>
                    <Button variant="secondary" onClick={() => navigate('/')}>
                        Continue Shopping
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-24">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-8">
                        {cart.items.map((item) => (
                            <div key={item.id} className="flex gap-6 py-6 border-b border-gray-100 last:border-0">
                                {/* Image */}
                                <div className="w-24 h-32 md:w-32 md:h-40 bg-gray-100 flex-shrink-0 cursor-pointer" onClick={() => navigate(`/product/${item.product_id}`)}>
                                    {item.variant_details?.product_image ? (
                                        <img src={item.variant_details.product_image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No Img</div>
                                    )}
                                </div>

                                {/* Details */}
                                <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-serif text-lg leading-tight cursor-pointer hover:underline underline-offset-4" onClick={() => navigate(`/product/${item.product_id}`)}>
                                                {item.variant_details?.product_name || item.variant_details?.name}
                                            </h3>
                                            <p className="font-medium text-sm font-sans">{formatPrice(item.price)}</p>
                                        </div>
                                        <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">
                                            {item.variant_details?.name !== 'Default' ? item.variant_details?.name : 'Standard Variant'}
                                            {item.variant_details?.sku && ` / ${item.variant_details.sku}`}
                                        </p>
                                    </div>

                                    <div className="flex justify-between items-center mt-4">
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center border border-gray-200 rounded-full h-8 px-2">
                                                <button onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))} className="p-1 hover:bg-gray-100 rounded-full transition-colors"><Minus size={12} /></button>
                                                <span className="w-8 text-center text-xs font-medium font-sans">{item.quantity}</span>
                                                <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1 hover:bg-gray-100 rounded-full transition-colors"><Plus size={12} /></button>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFromCart(item.id)} className="text-xs text-gray-400 hover:text-red-500 uppercase tracking-widest transition-colors">
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Summary */}
                    <div className="h-fit space-y-8 sticky top-24">
                        <div className="bg-gray-50 p-8 rounded-sm">
                            <h2 className="font-serif text-2xl mb-6">Order Summary</h2>

                            <div className="space-y-4 text-sm mb-8 border-b border-gray-200 pb-8">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Subtotal</span>
                                    <span className="font-sans">{formatPrice(cart.total_price)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Tax (10%)</span>
                                    <span className="font-sans">{formatPrice(parseFloat(cart.total_price) * 0.1)}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                        <span>Discount</span>
                                        <span className="font-sans">-{formatPrice(discount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-serif text-lg pt-4 border-t border-gray-200 mt-4">
                                    <span>Total</span>
                                    <span className="font-mono font-medium">{formatPrice((parseFloat(cart.total_price) * 1.1 - discount))}</span>
                                </div>
                            </div>

                            {/* Coupon */}
                            <div className="mb-8">
                                <div className="flex items-end gap-2">
                                    <InputField
                                        label="Promo Code"
                                        id="coupon"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        placeholder="ENTER CODE"
                                        className="uppercase"
                                    />
                                    <Button variant="secondary" onClick={handleApplyCoupon} className="mb-[2px] py-2 px-4 text-[10px]">
                                        Apply
                                    </Button>
                                </div>
                                {couponError && <p className="text-red-500 text-[10px] mt-2 uppercase tracking-wide">{couponError}</p>}
                                {couponSuccess && <p className="text-green-600 text-[10px] mt-2 uppercase tracking-wide">{couponSuccess}</p>}
                            </div>

                            <Button
                                onClick={() => navigate('/checkout', { state: { couponCode, discount } })}
                                className="w-full justify-center"
                            >
                                Checkout
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CartPage;
